import React, { Suspense, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Button, Row, FormControl, OverlayTrigger, Tooltip } from 'react-bootstrap';
import MaskedFormControl from 'react-bootstrap-maskedinput';
import { Panel } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import OptionsHelper from '../../lib/OptionsHelper';
import Permission from '../../components/Permission/Permission';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import withAuthentication from '../../hoc/withAuthentication';
import withForm from '../../hoc/withForm';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useStateStore } from '../../store/providers/State';
import { useRealEstateBoardStore } from '../../store/providers/RealEstateBoard';

import validations from './validations';

import * as constants from '../../constants';
import './RealEstateBoard.css';

import Spinner from '../../components/Spinner/Spinner';
import { sanitizePhoneNumber } from '../../lib/Sanitizer';
import CheckboxInput from '../../components/CheckboxInput/CheckboxInput';

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const InputCollection = React.lazy(() => import('../../components/InputCollection/InputCollection'));

function RealEstateBoard(props) {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, newRecord, createRecord, updateRecord, data, clearData } = useRealEstateBoardStore();
    const { listRecords: stateList, data: stateData } = useStateStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [title, setTitle] = useState('');
    const [stateOptions, setStateOptions] = useState([]);

    const pagePermission = constants.PERMISSION_SETTINGS_REALESTATEBOARDS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const submittedHandler = (e) => {
        e.preventDefault();
        if (e.target.id !== 'realEstateBoard') { return; }

        var valid = props.formValidate(validations);

        if (valid) {
            if (_.isEmpty(props.formData.joinAssociationWebform)) {
                props.formChanged('joinAssociationWebform', '');
            }

            if (_.isEmpty(props.formData.brokerageTransferWebform)) {
                props.formChanged('brokerageTransferWebform', '');
            }

            if (props.formData.membershipPhoneNumber) {
                props.formData.membershipPhoneNumber = sanitizePhoneNumber(props.formData.membershipPhoneNumber);
            }

            props.formSubmitted();
            isNew ? createRecord(props.formData) : updateRecord({ ...props.formData, realEstateBoardId: props.formData.id });
        }
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (!isLoaded && isMounted &&
                data.scope === 'select' && data.status === 'success') {
                props.formInitialize(data.body);

                setTitle(`Edit Real Estate Board (Id: ${data.body.id})`);
                setIsLoaded(true);
                dispatch(breadcrumbAddRoute({ key: 'edit-realEstateBoard', text: 'Edit Real Estate Board' }));
            }

            if (!isNew && (data.scope === 'new' && data.status === 'success')) {
                setTitle(`New Real Estate Board`);
                dispatch(breadcrumbAddRoute({ key: 'new-realEstateBoard', text: 'New Real Estate Board' }));
            }

            if ((data.scope === 'create' || data.scope === 'update') && data.status === 'success') {
                navigate('/realEstateBoards');
            }

            if (!isNew && (data.scope === 'new' && data.status === 'success')) { setIsNew(true); }
            setIsBusy(data.status === 'processing' && (data.scope === 'update' || data.scope === 'create'));
        }
    }, [props, data, addAlert, clearData, isLoaded, isMounted, isNew, stateOptions, dispatch]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        stateList();

        if (!_.isEmpty(props.realEstateBoardId)) {
            selectRecord({ realEstateBoardId: props.realEstateBoardId });
        } else {
            newRecord();
        }

        dispatch(breadcrumbAddRoute({ key: 'realEstateBoards', parent: 'Settings', text: 'Real Estate Boards', path: '/realEstateBoards' }));

        setIsMounted(true);
    }, [isMounted, hasPermission, dispatch, selectRecord, newRecord, props.realEstateBoardId, stateList]);

    useEffect(() => {
        if (props.formClosing) { navigate('/realEstateBoards'); }
    }, [props]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(stateData) && stateData.scope === 'list' && stateData.status === 'success') {
            setStateOptions(OptionsHelper({ list: stateData.body, includeBlank: true }));
        }
    }, [stateData]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title={title} />
                            <Panel>
                                <Panel.Body>
                                    {
                                        (!isLoaded && !isNew) && <Spinner text="Loading Real Estate Board..." />
                                    }
                                    {
                                        (isLoaded || isNew) && (
                                            <Form id="realEstateBoard" onSubmit={(e) => submittedHandler(e)}>
                                                <Row>
                                                    <div className="col-md-6">
                                                        <InputCollection required labelText="Real Estate Board Name" errorMessage={props.formValidationCheck('name')}>
                                                            <FormControl id="name" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={50} defaultValue={props.formData['name'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                </Row>
                                                <Row>
                                                    <div className="col-md-3">
                                                        <InputCollection required labelText="State" errorMessage={props.formValidationCheck('stateId')}>
                                                            <FormControl id="stateId" componentClass="select" onChange={(e) => props.formChanged(e.target.id, e.target.value)} defaultValue={props.formData['stateId'] || ''} disabled={isBusy}>
                                                                {stateOptions}
                                                            </FormControl>
                                                        </InputCollection>
                                                    </div>
                                                    <div className="col-md-5 padding-top-30">
                                                        <div className='flex-align-center'>
                                                            <CheckboxInput
                                                                id="isNARAffiliated"
                                                                value={props.formData['isNARAffiliated']}
                                                                disabled={isBusy} 
                                                                labelText="NAR Affiliated"
                                                                changed={(id, value) => props.formChanged(id, !value)}
                                                            />
                                                             <OverlayTrigger placement="top" overlay={<Tooltip>Check if the Association is NAR Affiliated. If checked, the Agent’s profile will have the Licensed Realtor checkbox marked when the Association is active in their RSB profile</Tooltip>}>
                                                                <i style={{ marginBottom: '2px' }} className="fa fa-question-circle form-label-tip-margin"></i>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                </Row>
                                                <hr className="form-divider" />
                                                <Row>
                                                    <div className="col-md-2">
                                                        <InputCollection labelText="Contact First Name" errorMessage={props.formValidationCheck('contactFirstName')}>
                                                            <FormControl id="contactFirstName" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={50} defaultValue={props.formData['contactFirstName'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <InputCollection labelText="Contact Last Name" errorMessage={props.formValidationCheck('contactLastName')}>
                                                            <FormControl id="contactLastName" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={50} defaultValue={props.formData['contactLastName'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                </Row>
                                                <hr className="form-divider" />
                                                <Row>
                                                    <div className="col-md-6">
                                                        <InputCollection labelText="Address" errorMessage={props.formValidationCheck('address1')}>
                                                            <FormControl id="address1" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={255} defaultValue={props.formData['address1'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                </Row>
                                                <Row>
                                                    <div className="col-md-6">
                                                        <InputCollection labelText="Address (additional)" errorMessage={props.formValidationCheck('address2')}>
                                                            <FormControl id="address2" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={255} defaultValue={props.formData['address2'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                </Row>
                                                <Row>
                                                    <div className="col-md-3">
                                                        <InputCollection labelText="City" errorMessage={props.formValidationCheck('addressCity')}>
                                                            <FormControl id="addressCity" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={100} defaultValue={props.formData['addressCity'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <InputCollection labelText="State" errorMessage={props.formValidationCheck('addressStateId')}>
                                                            <FormControl id="addressStateId" componentClass="select" onChange={(e) => props.formChanged(e.target.id, e.target.value)} defaultValue={props.formData['addressStateId'] || ''} disabled={isBusy}>
                                                                {stateOptions}
                                                            </FormControl>
                                                        </InputCollection>
                                                    </div>
                                                    <div className="col-md-1">
                                                        <InputCollection labelText="Zip Code" errorMessage={props.formValidationCheck('addressZipCode')}>
                                                            <FormControl id="addressZipCode" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={10} defaultValue={props.formData['addressZipCode'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                </Row>
                                                <hr className="form-divider" />
                                                <Row>
                                                    <div className="col-md-2">
                                                        <InputCollection labelText="Membership Phone Number" errorMessage={props.formValidationCheck('membershipPhoneNumber')}>
                                                            <MaskedFormControl id="membershipPhoneNumber" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={15} mask="(111) 111-1111" value={props.formData['membershipPhoneNumber'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                </Row>
                                                <Row>
                                                    <div className="col-md-3">
                                                        <InputCollection labelText="Join Association Webform" errorMessage={props.formValidationCheck('joinAssociationWebform')}>
                                                            <FormControl id="joinAssociationWebform" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={100} defaultValue={props.formData['joinAssociationWebform'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <InputCollection labelText="Brokerage Transfer Webform" errorMessage={props.formValidationCheck('brokerageTransferWebform')}>
                                                            <FormControl id="brokerageTransferWebform" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={100} defaultValue={props.formData['brokerageTransferWebform'] || ''} disabled={isBusy} />
                                                        </InputCollection>
                                                    </div>
                                                </Row>
                                                <hr className="form-divider" />
                                                <div className="form-action-buttons">
                                                    <Button bsStyle="default" onClick={() => props.formCancelled()} className="button-group-margin" disabled={isBusy}>
                                                        <i className="fa fa-undo button-icon"></i>
                                                        Back
                                                    </Button>
                                                    <Button bsStyle="danger" variant="danger" type="submit" className="button-group-margin" disabled={!canEdit || isBusy}>
                                                        <i className="fa fa-save button-icon"></i>
                                                        {isBusy ? 'Saving...' : 'Save & Exit'}
                                                    </Button>
                                                </div>
                                            </Form>
                                        )
                                    }
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(withForm(RealEstateBoard)));
